import React from "react";
import {
  Text,
  makeStyles,
  tokens,
  Card,
  CardHeader,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { JobActivity } from "../../models/Job";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxHeight: "300px",
    overflowY: "auto",
  },
  activityItem: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  timestamp: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    minWidth: "80px",
    flexShrink: 0,
  },
  message: {
    fontSize: tokens.fontSizeBase200,
    flex: 1,
  },
  infoMessage: {
    color: tokens.colorNeutralForeground1,
  },
  warningMessage: {
    color: tokens.colorPaletteMarigoldForeground1,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
  },
  documentsCount: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalS,
  },
  emptyState: {
    textAlign: "center",
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
});

interface ReindexActivityLogProps {
  activities: JobActivity[];
  title?: string;
}

export const ReindexActivityLog: React.FC<ReindexActivityLogProps> = ({
  activities,
  title,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Text weight="semibold">
            {title || t("reindexActivityLog.title")}
          </Text>
        </CardHeader>
        <Text className={classes.emptyState}>
          {t("reindexActivityLog.noActivities")}
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Text weight="semibold">{title || t("reindexActivityLog.title")}</Text>
      </CardHeader>
      <div className={classes.container}>
        {activities.map((activity, index) => (
          <div key={index} className={classes.activityItem}>
            <Text className={classes.timestamp}>
              {formatTimestamp(activity.timestamp)}
            </Text>
            <div>
              <Text className={classes.message}>{activity.action}</Text>
              {activity.details && (
                <Text className={classes.documentsCount}>
                  {activity.details}
                </Text>
              )}
              {activity.itemsCount > 0 && (
                <Text className={classes.documentsCount}>
                  ({activity.itemsCount} {t("reindexActivityLog.items")})
                </Text>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
